import { BaseAPIService } from '../../../../services/baseapiservice';
import indexedDBService, { INDEXED_DB_STORES } from '../../../../utils/indexedDBService';

// Type definitions for GIS API responses
interface APILayerGroup {
  group_id: number;
  group_code: string;
  group_name: string;
  group_name_local: string;
  group_description: string;
  group_icon: string;
  group_display_order: number;
  group_sequence_id: number;
  layers: APILayer[];
}

interface APILayer {
  id: number;
  layer_code: string;
  layer_name: string;
  layer_name_local: string;
  description: string;
  table_name: string;
  geometry_column: string;
  geometry_type: string;
  filter_column: string | null;
  filter_sql: string | null;
  default_filter_value: string | null;
  default_style: any;
  is_clustered: boolean;
  cluster_distance: number;
  min_zoom: number;
  max_zoom: number;
  icon: string;
  layer_order: number;
  sequence_id: number;
  is_active: boolean;
}

interface APIBaseLayer {
  id: number;
  layer_code: string;
  layer_name: string;
  layer_name_local: string;
  description: string;
  layer_type: string;
  layer_category: string;
  tile_url: string;
  tile_subdomains: string[];
  wms_url: string;
  wms_layers: string;
  attribution: string;
  max_zoom: number;
  min_zoom: number;
  is_default: boolean;
  icon: string;
  display_order: number;
  sequence_id: number;
}

interface APIDataLayer {
  id: number;
  layer_code: string;
  layer_name: string;
  layer_name_local: string;
  description: string;
  data_source_type: string;
  table_name: string;
  api_endpoint: string;
  geometry_column: string;
  geometry_type: string;
  symbol_type: string;
  symbol_field: string;
  symbol_config: any;
  popup_template: string;
  popup_fields: string[];
  icon: string;
  display_order: number;
  sequence_id: number;
}

export interface ImportExportHistory {
  history_id: number;
  operation_type: 'import' | 'export';
  file_name: string;
  file_format: string;
  file_path?: string;
  file_url?: string;
  file_size?: number;
  record_count: number;
  target_table_name?: string;
  layer_code?: string;
  layer_name?: string;
  operation_status: 'success' | 'failed' | 'in_progress';
  error_message?: string;
  source_columns?: any;
  mapped_columns?: any;
  operation_metadata?: any;
  clnt_id?: number;
  crte_usr_id?: number;
  updte_usr_id?: number;
  a_in: boolean;
  d_ts?: string;
  u_ts: string;
  i_ts: string;
}

export interface CreateHistoryData {
  operation_type: 'import' | 'export';
  file_name: string;
  file_format: string;
  file_path?: string;
  file_url?: string;
  file_size?: number;
  record_count?: number;
  target_table_name?: string;
  layer_code?: string;
  layer_name?: string;
  operation_status?: 'success' | 'failed' | 'in_progress';
  error_message?: string;
  source_columns?: any;
  mapped_columns?: any;
  operation_metadata?: any;
}

export interface UpdateHistoryData {
  operation_status?: 'success' | 'failed' | 'in_progress';
  file_path?: string;
  file_url?: string;
  file_size?: number;
  record_count?: number;
  target_table_name?: string;
  error_message?: string;
  source_columns?: any;
  mapped_columns?: any;
  operation_metadata?: any;
}

// Type definitions for component use
export interface LayerItem {
  id: string;
  name: string;
  icon: string;
  color: string;
  description: string;
  clustering?: boolean;
  geometryType: 'point' | 'line' | 'polygon';
  lineWidth?: number;
}

export interface LayerCategories {
  [categoryName: string]: LayerItem[];
}

class GISService extends BaseAPIService {
  // Cache for layer data to avoid redundant API calls
  private layerDataCache: Map<string, any> = new Map();
  // Cache for layer styles to avoid redundant API calls
  private layerStyleCache: Map<string, any> = new Map();

  // Cache for Pull GIS Records feature
  private pullGisCategoriesCache: any[] | null = null;
  private pullGisRecordsCache: Map<number, any[]> = new Map();

  /**
   * Get all feature layers grouped by layer groups
   */
  async getFeatureLayersGrouped(): Promise<{ status: number; message: string | null; data: APILayerGroup[] }> {
    try {
      const response = await this.get('/gis/feature-layers/grouped');
      return response.data;
    } catch (error) {
      console.error('Error fetching grouped feature layers:', error);
      throw error;
    }
  }

  /**
   * Get map layers grouped by layer group for a specific map handler
   * Uses gis_layers_map_rel_t to return only layers assigned to the map
   */
  async getMapLayersGrouped(handler: string) {
    try {
      const response = await this.get(`/gis/maps/handler/${handler}/layers/grouped`);
      return response.data;
    } catch {
      // Map layers are optional — return empty so callers degrade gracefully
      return { status: 200, data: [] };
    }
  }

  /**
   * Get all layer groups
   */
  async getAllLayerGroups(): Promise<any> {
    try {
      const response = await this.get('/gis/layer-groups');
      return response.data;
    } catch (error) {
      console.error('Error fetching layer groups:', error);
      throw error;
    }
  }

  /**
   * Get layer group by ID
   */
  async getLayerGroupById(id: number): Promise<any> {
    try {
      const response = await this.get(`/gis/layer-groups/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching layer group ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create new layer group
   */
  async createLayerGroup(groupData: any): Promise<any> {
    try {
      const response = await this.post('/gis/layer-groups', groupData);
      return response.data;
    } catch (error) {
      console.error('Error creating layer group:', error);
      throw error;
    }
  }

  /**
   * Update layer group
   */
  async updateLayerGroup(id: number, groupData: any): Promise<any> {
    try {
      const response = await this.put(`/gis/layer-groups/${id}`, groupData);
      return response.data;
    } catch (error) {
      console.error(`Error updating layer group ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete layer group (soft delete)
   */
  async deleteLayerGroup(id: number): Promise<any> {
    try {
      const response = await this.delete(`/gis/layer-groups/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting layer group ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get all base layers
   */
  async getAllBaseLayers(): Promise<{ status: number; message: string | null; data: APIBaseLayer[] }> {
    try {
      const response = await this.get('/gis/base-layers');
      return response.data;
    } catch (error) {
      console.error('Error fetching base layers:', error);
      throw error;
    }
  }

  /**
   * Convenience wrapper for getAllBaseLayers
   */
  async getBaseLayers(): Promise<{ status: number; message: string | null; data: APIBaseLayer[] }> {
    return this.getAllBaseLayers();
  }

  /**
   * Get base layer by ID
   */
  async getBaseLayerById(id: number): Promise<any> {
    const response = await this.get(`/gis/base-layers/${id}`);
    return response.data;
  }

  /**
   * Create new base layer
   */
  async createBaseLayer(layerData: any): Promise<any> {
    const response = await this.post('/gis/base-layers', layerData);
    return response.data;
  }

  /**
   * Update base layer
   */
  async updateBaseLayer(id: number, layerData: any): Promise<any> {
    const response = await this.put(`/gis/base-layers/${id}`, layerData);
    return response.data;
  }

  /**
   * Delete base layer
   */
  async deleteBaseLayer(id: number): Promise<any> {
    const response = await this.delete(`/gis/base-layers/${id}`);
    return response.data;
  }

  /**
   * Set base layer as default
   */
  async setDefaultBaseLayer(id: number, category: string): Promise<any> {
    const response = await this.put(`/gis/base-layers/${id}/set-default`, { category });
    return response.data;
  }

  /**
   * Get all data layers
   */
  async getAllDataLayers(): Promise<{ status: number; message: string | null; data: APIDataLayer[] }> {
    try {
      const response = await this.get('/gis/data-layers');
      return response.data;
    } catch (error) {
      console.error('Error fetching data layers:', error);
      throw error;
    }
  }

  /**
   * Convenience wrapper for getAllDataLayers
   */
  async getDataLayers(): Promise<{ status: number; message: string | null; data: APIDataLayer[] }> {
    return this.getAllDataLayers();
  }

  /**
   * Get data layers filtered by category
   */
  async getDataLayersByCategory(category: string): Promise<{ status: number; message: string | null; data: APIDataLayer[] }> {
    try {
      const response = await this.get(`/gis/data-layers/category/${category}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching data layers for category ${category}:`, error);
      throw error;
    }
  }

  /**
   * Get data layers for Government Assets
   */
  async getGovernmentAssetsLayers(): Promise<{ status: number; message: string | null; data: APIDataLayer[] }> {
    return this.getDataLayersByCategory('government_assets');
  }

  /**
   * Get data layers for Road Network
   */
  async getRoadNetworkLayers(): Promise<{ status: number; message: string | null; data: APIDataLayer[] }> {
    return this.getDataLayersByCategory('road_network');
  }

  /**
   * Get data layers for Water Network
   */
  async getWaterNetworkLayers(): Promise<{ status: number; message: string | null; data: APIDataLayer[] }> {
    return this.getDataLayersByCategory('water_network');
  }

  /**
   * Get data layers for Sewerage Network
   */
  async getSewerageNetworkLayers(): Promise<{ status: number; message: string | null; data: APIDataLayer[] }> {
    return this.getDataLayersByCategory('sewerage_network');
  }

  /**
   * Get data layers for Greening & Beautification
   */
  async getGreeningBeautificationLayers(): Promise<{ status: number; message: string | null; data: APIDataLayer[] }> {
    return this.getDataLayersByCategory('greening_beautification');
  }

  /**
   * Get data layers for Property Tax
   */
  async getPropertyTaxLayers(): Promise<{ status: number; message: string | null; data: APIDataLayer[] }> {
    return this.getDataLayersByCategory('property_tax');
  }

  /**
   * Get data layers for ReSurvey
   */
  async getReSurveyLayers(): Promise<{ status: number; message: string | null; data: APIDataLayer[] }> {
    return this.getDataLayersByCategory('resurvey');
  }

  /**
   * Get data layers for Digital Door No
   */
  async getDigitalDoorNoLayers(): Promise<{ status: number; message: string | null; data: APIDataLayer[] }> {
    return this.getDataLayersByCategory('digital_door_no');
  }

  /**
   * Get data layers for Trade Licenses
   */
  async getTradeLicensesLayers(): Promise<{ status: number; message: string | null; data: APIDataLayer[] }> {
    return this.getDataLayersByCategory('trade_licenses');
  }

  /**
   * Get data layers for Street Vendors
   */
  async getStreetVendorsLayers(): Promise<{ status: number; message: string | null; data: APIDataLayer[] }> {
    return this.getDataLayersByCategory('street_vendors');
  }

  /**
   * Get data layer by ID
   */
  async getDataLayerById(id: number): Promise<any> {
    const response = await this.get(`/gis/data-layers/${id}`);
    return response.data;
  }

  /**
   * Create new data layer
   */
  async createDataLayer(layerData: any): Promise<any> {
    const response = await this.post('/gis/data-layers', layerData);
    return response.data;
  }

  /**
   * Update data layer
   */
  async updateDataLayer(id: number, layerData: any): Promise<any> {
    const response = await this.put(`/gis/data-layers/${id}`, layerData);
    return response.data;
  }

  /**
   * Delete data layer
   */
  async deleteDataLayer(id: number): Promise<any> {
    const response = await this.delete(`/gis/data-layers/${id}`);
    return response.data;
  }

  /**
   * Get all external applications
   */
  async getAllExternalApplications(): Promise<any> {
    const response = await this.get('/gis/external-applications');
    return response.data;
  }

  /**
   * Get external applications by type
   */
  async getExternalApplicationsByType(type: string): Promise<any> {
    const response = await this.get(`/gis/external-applications/type/${type}`);
    return response.data;
  }

  /**
   * Get external application by ID
   */
  async getExternalApplicationById(id: number): Promise<any> {
    const response = await this.get(`/gis/external-applications/${id}`);
    return response.data;
  }

  /**
   * Create new external application
   */
  async createExternalApplication(appData: any): Promise<any> {
    const response = await this.post('/gis/external-applications', appData);
    return response.data;
  }

  /**
   * Update external application
   */
  async updateExternalApplication(id: number, appData: any): Promise<any> {
    const response = await this.put(`/gis/external-applications/${id}`, appData);
    return response.data;
  }

  /**
   * Delete external application
   */
  async deleteExternalApplication(id: number): Promise<any> {
    const response = await this.delete(`/gis/external-applications/${id}`);
    return response.data;
  }

  /**
   * Test external application connection
   */
  async testExternalApplicationConnection(id: number): Promise<any> {
    const response = await this.post(`/gis/external-applications/${id}/test-connection`, {});
    return response.data;
  }

  /**
   * Get feature layers by group ID
   */
  async getFeatureLayersByGroup(groupId: number): Promise<any> {
    try {
      const response = await this.get(`/gis/feature-layers/group/${groupId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching feature layers for group ${groupId}:`, error);
      throw error;
    }
  }

  /**
   * Get all feature layers (not grouped)
   */
  async getAllFeatureLayers(filters?: { group_id?: number }): Promise<any> {
    try {
      const params = new URLSearchParams();
      if (filters?.group_id) params.append('group_id', filters.group_id.toString());
      const url = `/gis/feature-layers${params.toString() ? '?' + params.toString() : ''}`;
      const response = await this.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching all feature layers:', error);
      throw error;
    }
  }

  /**
   * Get pull GIS categories for Pull GIS Records feature
   * Returns all active layers with group information
   * Uses cache if available, otherwise fetches from API
   * @param forceRefresh - If true, bypasses cache and fetches fresh data
   */
  async getPullGisCategories(forceRefresh: boolean = false): Promise<any> {
    try {
      // Return cached data if available and not forcing refresh
      if (!forceRefresh && this.pullGisCategoriesCache) {
        console.log('[gisService] Returning cached pull GIS categories');
        return { status: 200, data: this.pullGisCategoriesCache };
      }

      console.log('[gisService] Fetching pull GIS categories from API');
      const response = await this.get('/gis/pull-gis-categories');

      // Cache the categories data
      if (response.data?.status === 200 && response.data?.data) {
        this.pullGisCategoriesCache = response.data.data;
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching pull GIS categories:', error);
      throw error;
    }
  }

  /**
   * Get pull GIS records for a specific layer
   * Returns master table records joined with SVM relation table and adm_svm_lst_t
   * Uses cache if available, otherwise fetches from API
   * @param lyrId - Layer ID
   * @param forceRefresh - If true, bypasses cache and fetches fresh data
   */
  async getPullGisRecords(lyrId: number, forceRefresh: boolean = false): Promise<any> {
    try {
      // Return cached data if available and not forcing refresh
      if (!forceRefresh && this.pullGisRecordsCache.has(lyrId)) {
        console.log(`[gisService] Returning cached pull GIS records for layer ${lyrId}`);
        return { status: 200, data: this.pullGisRecordsCache.get(lyrId) };
      }

      console.log(`[gisService] Fetching pull GIS records for layer ${lyrId} from API`);
      const response = await this.get(`/gis/pull-gis-records/${lyrId}`);

      // Cache the records data
      if (response.data?.status === 200 && response.data?.data) {
        this.pullGisRecordsCache.set(lyrId, response.data.data);
      }

      return response.data;
    } catch (error) {
      console.error(`Error fetching pull GIS records for layer ${lyrId}:`, error);
      throw error;
    }
  }

  /**
   * Clear pull GIS cache
   * @param lyrId - If provided, clears only records cache for that layer. Otherwise clears all.
   */
  clearPullGisCache(lyrId?: number): void {
    if (lyrId !== undefined) {
      this.pullGisRecordsCache.delete(lyrId);
      console.log(`[gisService] Cleared pull GIS records cache for layer ${lyrId}`);
    } else {
      this.pullGisCategoriesCache = null;
      this.pullGisRecordsCache.clear();
      console.log('[gisService] Cleared all pull GIS cache');
    }
  }

  /**
   * Check if pull GIS categories are cached
   */
  hasPullGisCategoriesCache(): boolean {
    return this.pullGisCategoriesCache !== null;
  }

  /**
   * Get pull GIS categories directly from cache (no API call)
   * Returns null if cache is empty
   */
  getPullGisCategoriesFromCache(): any[] | null {
    return this.pullGisCategoriesCache;
  }

  /**
   * Check if pull GIS records are cached for a layer
   */
  hasPullGisRecordsCache(lyrId: number): boolean {
    return this.pullGisRecordsCache.has(lyrId);
  }

  /**
   * Get pull GIS records directly from cache for a layer (no API call)
   * Returns null if cache is empty for that layer
   */
  getPullGisRecordsFromCache(lyrId: number): any[] | null {
    return this.pullGisRecordsCache.get(lyrId) || null;
  }

  /**
   * Get feature layer by ID
   */
  async getFeatureLayerById(id: number): Promise<any> {
    try {
      const response = await this.get(`/gis/feature-layers/id/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching feature layer ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create new feature layer
   */
  async createFeatureLayer(layerData: any): Promise<any> {
    try {
      const response = await this.post('/gis/feature-layers', layerData);
      // Clear cache after creating a layer
      await this.clearLayerCache();
      return response.data;
    } catch (error) {
      console.error('Error creating feature layer:', error);
      throw error;
    }
  }

  /**
   * Update existing feature layer
   */
  async updateFeatureLayer(id: number, layerData: any): Promise<any> {
    try {
      const response = await this.put(`/gis/feature-layers/${id}`, layerData);
      // Clear cache after updating a layer
      await this.clearLayerCache();
      return response.data;
    } catch (error) {
      console.error(`Error updating feature layer ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete feature layer (soft delete)
   */
  async deleteFeatureLayer(id: number): Promise<any> {
    try {
      const response = await this.delete(`/gis/feature-layers/${id}`);
      // Clear cache after deleting a layer
      await this.clearLayerCache();
      return response.data;
    } catch (error) {
      console.error(`Error deleting feature layer ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get layer by code
   */
  async getLayerByCode(layerCode: string): Promise<any> {
    try {
      const response = await this.get(`/gis/feature-layers/${layerCode}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching layer ${layerCode}:`, error);
      throw error;
    }
  }

  /**
   * Get layer geometry data as GeoJSON (with persistent IndexedDB caching)
   */
  async getLayerData(layerCode: string): Promise<any> {
    try {
      // Helper: check if GeoJSON response has actual features
      const hasFeatures = (data: any): boolean => {
        if (!data) return false;
        // Check common response shapes for non-empty features
        const fc = data?.data || data;
        if (fc?.type === 'FeatureCollection' && Array.isArray(fc.features)) {
          return fc.features.length > 0;
        }
        if (Array.isArray(fc) && fc.length > 0) return true;
        if (fc?.features && Array.isArray(fc.features)) return fc.features.length > 0;
        return false;
      };

      // 1. Check memory cache first (fastest)
      if (this.layerDataCache.has(layerCode)) {
        const memCached = this.layerDataCache.get(layerCode);
        // Skip empty cached responses — re-fetch in case data was loaded since last cache
        if (hasFeatures(memCached)) {
          console.log(`[gisService] Returning memory-cached data for layer: ${layerCode}`);
          return memCached;
        }
        console.log(`[gisService] Memory cache for ${layerCode} is empty, re-fetching`);
        this.layerDataCache.delete(layerCode);
      }

      // 2. Check IndexedDB cache (persistent across refreshes)
      const cachedData = await indexedDBService.get(
        INDEXED_DB_STORES.LAYER_DATA,
        layerCode
      );

      if (cachedData) {
        // Skip empty cached responses — data may have been imported since last cache
        if (hasFeatures(cachedData)) {
          console.log(`[gisService] Returning IndexedDB-cached data for layer: ${layerCode}`);
          // Also store in memory cache for faster subsequent access
          this.layerDataCache.set(layerCode, cachedData);
          return cachedData;
        }
        console.log(`[gisService] IndexedDB cache for ${layerCode} is empty, re-fetching`);
        await indexedDBService.delete(INDEXED_DB_STORES.LAYER_DATA, layerCode);
      }

      // 3. Fetch from API if not cached anywhere
      console.log(`[gisService] Fetching fresh data from API for layer: ${layerCode}`);
      const response = await this.get(`/gis/layer-data/${layerCode}`);

      // 4. Only cache responses that contain actual features
      if (hasFeatures(response.data)) {
        this.layerDataCache.set(layerCode, response.data);
        await indexedDBService.set(
          INDEXED_DB_STORES.LAYER_DATA,
          layerCode,
          response.data
        );
        console.log(`[gisService] Cached data in memory and IndexedDB for layer: ${layerCode}`);
      } else {
        console.log(`[gisService] Layer ${layerCode} returned empty — not caching`);
      }

      return response.data;
    } catch (error: any) {
      console.warn(`[gisService] Layer data unavailable for ${layerCode}: ${error?.message || error}`);
      throw error;
    }
  }

  /**
   * Clear cached data for a specific layer or all layers (both memory and IndexedDB)
   */
  async clearLayerCache(layerCode?: string): Promise<void> {
    if (layerCode) {
      // Clear from memory cache
      this.layerDataCache.delete(layerCode);
      // Clear from IndexedDB
      await indexedDBService.delete(INDEXED_DB_STORES.LAYER_DATA, layerCode);
      console.log(`[gisService] Cleared cache for layer: ${layerCode}`);
    } else {
      // Clear all from memory cache
      this.layerDataCache.clear();
      // Clear all from IndexedDB for current user
      await indexedDBService.clearStore(INDEXED_DB_STORES.LAYER_DATA);
      console.log(`[gisService] Cleared all layer cache`);
    }
  }

  /**
   * Check if layer data is cached
   */
  isLayerCached(layerCode: string): boolean {
    return this.layerDataCache.has(layerCode);
  }

  /**
   * Get cache size (number of cached layers)
   */
  getCacheSize(): number {
    return this.layerDataCache.size;
  }

  /**
   * Get layer style configuration (with persistent IndexedDB caching)
   */
  async getLayerStyle(layerCode: string): Promise<any> {
    try {
      // 1. Check memory cache first (fastest)
      if (this.layerStyleCache.has(layerCode)) {
        console.log(`[gisService] Returning memory-cached style for layer: ${layerCode}`);
        return this.layerStyleCache.get(layerCode);
      }

      // 2. Check IndexedDB cache (persistent across refreshes)
      const cachedStyle = await indexedDBService.get(
        INDEXED_DB_STORES.LAYER_STYLES,
        layerCode
      );

      if (cachedStyle) {
        console.log(`[gisService] Returning IndexedDB-cached style for layer: ${layerCode}`);
        // Also store in memory cache for faster subsequent access
        this.layerStyleCache.set(layerCode, cachedStyle);
        return cachedStyle;
      }

      // 3. Fetch from API if not cached anywhere
      console.log(`[gisService] Fetching fresh style from API for layer: ${layerCode}`);
      const response = await this.get(`/gis/layer-style/${layerCode}`);

      // 4. Cache in both memory and IndexedDB
      this.layerStyleCache.set(layerCode, response.data);
      await indexedDBService.set(
        INDEXED_DB_STORES.LAYER_STYLES,
        layerCode,
        response.data
      );
      console.log(`[gisService] Cached style in memory and IndexedDB for layer: ${layerCode}`);

      return response.data;
    } catch (error) {
      console.error(`Error fetching layer style for ${layerCode}:`, error);
      throw error;
    }
  }

  /**
   * Check if layer style is cached
   */
  isLayerStyleCached(layerCode: string): boolean {
    return this.layerStyleCache.has(layerCode);
  }

  /**
   * Clear cached style for a specific layer or all layers (both memory and IndexedDB)
   */
  async clearLayerStyleCache(layerCode?: string): Promise<void> {
    if (layerCode) {
      // Clear from memory cache
      this.layerStyleCache.delete(layerCode);
      // Clear from IndexedDB
      await indexedDBService.delete(INDEXED_DB_STORES.LAYER_STYLES, layerCode);
      console.log(`[gisService] Cleared style cache for layer: ${layerCode}`);
    } else {
      // Clear all from memory cache
      this.layerStyleCache.clear();
      // Clear all from IndexedDB for current user
      await indexedDBService.clearStore(INDEXED_DB_STORES.LAYER_STYLES);
      console.log(`[gisService] Cleared all layer style cache`);
    }
  }

  /**
   * Get style cache size (number of cached layer styles)
   */
  getStyleCacheSize(): number {
    return this.layerStyleCache.size;
  }

  /**
   * Update layer style configuration
   */
  async updateLayerStyle(layerCode: string, styleConfig: any): Promise<any> {
    try {
      console.log(`[gisService] Updating style for layer: ${layerCode}`, styleConfig);
      const response = await this.put(`/gis/layer-style/${layerCode}`, styleConfig);
      console.log(`[gisService] Layer style update response:`, response);

      // Clear both layer data and style cache since style affects how data is displayed
      this.clearLayerCache(layerCode);
      this.clearLayerStyleCache(layerCode);
      console.log(`[gisService] Cleared cache for layer: ${layerCode}`);

      return response.data;
    } catch (error) {
      console.error(`Error updating layer style for ${layerCode}:`, error);
      throw error;
    }
  }

  /**
   * Transform API layer group data to component format
   */
  transformToLayerCategories(apiData: APILayerGroup[]): LayerCategories {
    const layerCategories: LayerCategories = {};

    apiData.forEach(group => {
      const layers: LayerItem[] = group.layers.map(layer => {
        // Map geometry types to component format
        let geometryType: 'point' | 'line' | 'polygon' = 'point';
        if (layer.geometry_type) {
          const geomType = layer.geometry_type.toLowerCase();
          if (geomType.includes('point')) {
            geometryType = 'point';
          } else if (geomType.includes('line') || geomType.includes('string')) {
            geometryType = 'line';
          } else if (geomType.includes('polygon')) {
            geometryType = 'polygon';
          }
        }

        // Extract color from default_style or use group color
        let color = '#3B82F6'; // default blue
        if (layer.default_style && layer.default_style.color) {
          color = layer.default_style.color;
        }

        // Extract line width from default_style
        let lineWidth = undefined;
        if (layer.default_style && layer.default_style.weight) {
          lineWidth = layer.default_style.weight;
        }

        // Map icon from database to remix icon
        const iconMap: { [key: string]: string } = {
          'map-marked-alt': 'ri-map-2-line',
          'map': 'ri-map-line',
          'map-pin': 'ri-compass-3-line',
          'home': 'ri-home-4-line',
          'road': 'ri-road-line',
          'drop': 'ri-drop-line',
          'water': 'ri-water-flash-line',
          'tree': 'ri-tree-line',
          'building': 'ri-building-line',
          'hospital': 'ri-hospital-line',
          'school': 'ri-school-line',
          'fire-station': 'ri-fire-line',
          'police': 'ri-police-car-line',
          'trash': 'ri-delete-bin-line',
          'lightbulb': 'ri-lightbulb-line',
          'bridge': 'ri-building-2-line',
          'park': 'ri-plant-line'
        };

        const icon = iconMap[layer.icon] || `ri-${layer.icon}` || 'ri-map-pin-line';

        return {
          id: layer.layer_code,
          name: layer.layer_name,
          icon: icon,
          color: color,
          description: layer.description || '',
          clustering: layer.is_clustered,
          geometryType: geometryType,
          lineWidth: lineWidth
        };
      });

      // Use group name as category key
      layerCategories[group.group_name] = layers;
    });

    return layerCategories;
  }

  /**
   * ========================================================================
   * IMPORT/EXPORT HISTORY METHODS
   * ========================================================================
   */

  /**
   * Get all import/export history records
   * @param filters Optional filters for querying history
   */
  async getAllHistory(filters?: {
    operation_type?: 'import' | 'export';
    operation_status?: 'success' | 'failed' | 'in_progress';
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ status: number; message: string | null; data: ImportExportHistory[] }> {
    try {
      const queryParams = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, value.toString());
          }
        });
      }

      const queryString = queryParams.toString();
      const url = queryString ? `/gis/import-export-history?${queryString}` : '/gis/import-export-history';

      const response = await this.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching import/export history:', error);
      throw error;
    }
  }

  /**
   * Get specific history record by ID
   */
  async getHistoryById(historyId: number): Promise<{ status: number; message: string | null; data: ImportExportHistory }> {
    try {
      const response = await this.get(`/gis/import-export-history/${historyId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching history record ${historyId}:`, error);
      throw error;
    }
  }

  /**
   * Create new import/export history record
   */
  async createHistory(historyData: CreateHistoryData): Promise<{ status: number; message: string | null; data: ImportExportHistory }> {
    try {
      const response = await this.post('/gis/import-export-history', historyData);
      return response.data;
    } catch (error) {
      console.error('Error creating history record:', error);
      throw error;
    }
  }

  /**
   * Update existing history record
   */
  async updateHistory(historyId: number, updateData: UpdateHistoryData): Promise<{ status: number; message: string | null; data: ImportExportHistory }> {
    try {
      const response = await this.put(`/gis/import-export-history/${historyId}`, updateData);
      return response.data;
    } catch (error) {
      console.error(`Error updating history record ${historyId}:`, error);
      throw error;
    }
  }

  /**
   * Delete history record (soft delete)
   */
  async deleteHistory(historyId: number): Promise<{ status: number; message: string | null; data: ImportExportHistory }> {
    try {
      const response = await this.delete(`/gis/import-export-history/${historyId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting history record ${historyId}:`, error);
      throw error;
    }
  }

  /**
   * Get import/export statistics
   */
  async getHistoryStatistics(filters?: {
    start_date?: string;
    end_date?: string;
  }): Promise<{ status: number; message: string | null; data: any[] }> {
    try {
      const queryParams = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, value.toString());
          }
        });
      }

      const queryString = queryParams.toString();
      const url = queryString ? `/gis/import-export-statistics?${queryString}` : '/gis/import-export-statistics';

      const response = await this.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching import/export statistics:', error);
      throw error;
    }
  }

  /**
   * Set the current user ID for user-specific caching in IndexedDB
   * Call this method when user logs in or changes
   */
  setUserId(userId: string): void {
    indexedDBService.setUserId(userId);
    console.log(`[gisService] User ID set to: ${userId}`);
  }

  /**
   * Get current user ID
   */
  getUserId(): string {
    return indexedDBService.getUserId();
  }

  /**
   * Get comprehensive cache statistics including IndexedDB
   */
  async getCacheStatistics(): Promise<{
    memory: {
      layerData: number;
      layerStyles: number;
    };
    indexedDB: Record<string, number>;
    total: number;
  }> {
    const indexedDBStats = await indexedDBService.getStats();
    const memoryStats = {
      layerData: this.layerDataCache.size,
      layerStyles: this.layerStyleCache.size
    };

    const total = Object.values(indexedDBStats).reduce((sum, count) => sum + count, 0);

    return {
      memory: memoryStats,
      indexedDB: indexedDBStats,
      total
    };
  }

  /**
   * Clear all caches (both memory and IndexedDB) for current user
   */
  async clearAllCaches(): Promise<void> {
    // Clear memory caches
    this.layerDataCache.clear();
    this.layerStyleCache.clear();

    // Clear IndexedDB caches for current user
    await indexedDBService.clearAll();

    console.log('[gisService] Cleared all caches (memory and IndexedDB)');
  }

  /**
   * Clean up expired entries from IndexedDB
   */
  async cleanupExpiredCache(): Promise<number> {
    const deletedCount = await indexedDBService.cleanupExpired();
    console.log(`[gisService] Cleaned up ${deletedCount} expired cache entries`);
    return deletedCount;
  }

  // ================================================================
  // MAP CONFIGURATION METHODS
  // ================================================================

  /**
   * Get all maps
   */
  async getAllMaps(): Promise<any> {
    try {
      const response = await this.get('/gis/maps');
      return response.data;
    } catch (error) {
      console.error('Error fetching all maps:', error);
      throw error;
    }
  }

  /**
   * Get maps for side menu
   */
  async getMapsForSideMenu(): Promise<any> {
    try {
      const response = await this.get('/gis/maps/side-menu');
      return response.data;
    } catch (error) {
      console.error('Error fetching side menu maps:', error);
      throw error;
    }
  }

  /**
   * Get map by handler
   */
  async getMapByHandler(handler: string): Promise<any> {
    try {
      const response = await this.get(`/gis/maps/handler/${handler}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching map by handler ${handler}:`, error);
      throw error;
    }
  }

  /**
   * Get map by ID
   */
  async getMapById(id: number): Promise<any> {
    try {
      const response = await this.get(`/gis/maps/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching map ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create new map
   */
  async createMap(mapData: any): Promise<any> {
    try {
      const response = await this.post('/gis/maps', mapData);
      return response.data;
    } catch (error) {
      console.error('Error creating map:', error);
      throw error;
    }
  }

  /**
   * Update existing map
   */
  async updateMap(id: number, mapData: any): Promise<any> {
    try {
      const response = await this.put(`/gis/maps/${id}`, mapData);
      return response.data;
    } catch (error) {
      console.error(`Error updating map ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete map (soft delete)
   */
  async deleteMap(id: number): Promise<any> {
    try {
      const response = await this.delete(`/gis/maps/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting map ${id}:`, error);
      throw error;
    }
  }

  // ================================================================
  // VECTOR TILE CACHE MANAGEMENT
  // ================================================================

  /**
   * Get all tile-enabled layers
   */
  async getTileEnabledLayers(): Promise<any> {
    try {
      const response = await this.get('/gis/tiles/layers');
      return response.data;
    } catch (error) {
      console.error('Error fetching tile-enabled layers:', error);
      throw error;
    }
  }

  /**
   * Get vector tile layers grouped by layer groups
   */
  async getVectorTileLayersGrouped(): Promise<any> {
    try {
      const response = await this.get('/gis/tiles/layers/grouped');
      return response.data;
    } catch (error) {
      console.error('Error fetching grouped vector tile layers:', error);
      throw error;
    }
  }

  /**
   * Get cache statistics
   */
  async getTileCacheStatistics(layerCode?: string): Promise<any> {
    try {
      const url = layerCode
        ? `/gis/tiles/cache/statistics?layerCode=${layerCode}`
        : '/gis/tiles/cache/statistics';
      const response = await this.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching cache statistics:', error);
      throw error;
    }
  }

  /**
   * Get cache hit rate
   */
  async getTileCacheHitRate(layerCode?: string): Promise<any> {
    try {
      const url = layerCode
        ? `/gis/tiles/cache/hit-rate?layerCode=${layerCode}`
        : '/gis/tiles/cache/hit-rate';
      const response = await this.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching cache hit rate:', error);
      throw error;
    }
  }

  /**
   * Invalidate cache for a layer
   */
  async invalidateTileCache(layerCode: string, zoom?: number): Promise<any> {
    try {
      const response = await this.post(`/gis/tiles/${layerCode}/invalidate`, { zoom });
      return response.data;
    } catch (error) {
      console.error(`Error invalidating cache for ${layerCode}:`, error);
      throw error;
    }
  }

  /**
   * Clean expired tiles
   */
  async cleanExpiredTiles(ttl?: number): Promise<any> {
    try {
      const response = await this.delete('/gis/tiles/cache/cleanup', { ttl });
      return response.data;
    } catch (error) {
      console.error('Error cleaning expired tiles:', error);
      throw error;
    }
  }

  /**
   * Optimize cache
   */
  async optimizeTileCache(layerCode?: string): Promise<any> {
    try {
      const response = await this.post('/gis/tiles/cache/optimize', { layerCode });
      return response.data;
    } catch (error) {
      console.error('Error optimizing cache:', error);
      throw error;
    }
  }

  /**
   * Get TileJSON metadata for a layer
   */
  async getTileJSON(layerCode: string): Promise<any> {
    try {
      const response = await this.get(`/gis/tiles/${layerCode}/tilejson.json`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching TileJSON for ${layerCode}:`, error);
      throw error;
    }
  }

  /**
   * Get layer bounds
   */
  async getTileLayerBounds(layerCode: string): Promise<any> {
    try {
      const response = await this.get(`/gis/tiles/${layerCode}/bounds`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching bounds for ${layerCode}:`, error);
      throw error;
    }
  }

  // ================================================================
  // DATA SOURCES MANAGEMENT
  // ================================================================

  /**
   * Get all data sources
   */
  async getAllDataSources(filters?: { is_active?: boolean; source_category?: string; type_id?: number }): Promise<any> {
    try {
      const params = new URLSearchParams();
      if (filters) {
        if (filters.is_active !== undefined) params.append('is_active', filters.is_active.toString());
        if (filters.source_category) params.append('source_category', filters.source_category);
        if (filters.type_id) params.append('type_id', filters.type_id.toString());
      }
      const url = `/gis/data-sources${params.toString() ? '?' + params.toString() : ''}`;
      const response = await this.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching data sources:', error);
      throw error;
    }
  }

  /**
   * Get data source by ID
   */
  async getDataSourceById(id: number): Promise<any> {
    try {
      const response = await this.get(`/gis/data-sources/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching data source ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get all data source types
   */
  async getDataSourceTypes(): Promise<any> {
    try {
      const response = await this.get('/gis/data-sources/types');
      return response.data;
    } catch (error) {
      console.error('Error fetching data source types:', error);
      throw error;
    }
  }

  /**
   * Create new data source
   */
  async createDataSource(dataSource: any): Promise<any> {
    try {
      const response = await this.post('/gis/data-sources', dataSource);
      return response.data;
    } catch (error) {
      console.error('Error creating data source:', error);
      throw error;
    }
  }

  /**
   * Update existing data source
   */
  async updateDataSource(id: number, dataSource: any): Promise<any> {
    try {
      const response = await this.put(`/gis/data-sources/${id}`, dataSource);
      return response.data;
    } catch (error) {
      console.error(`Error updating data source ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete data source
   */
  async deleteDataSource(id: number): Promise<any> {
    try {
      const response = await this.delete(`/gis/data-sources/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting data source ${id}:`, error);
      throw error;
    }
  }

  /**
   * Test data source connection
   */
  async testDataSourceConnection(dataSource: any): Promise<any> {
    try {
      const response = await this.post('/gis/data-sources/test-connection', dataSource);
      return response.data;
    } catch (error) {
      console.error('Error testing data source connection:', error);
      throw error;
    }
  }

  /**
   * Update data source health status
   */
  async updateDataSourceHealth(id: number, healthData: any): Promise<any> {
    try {
      const response = await this.put(`/gis/data-sources/${id}/health`, healthData);
      return response.data;
    } catch (error) {
      console.error(`Error updating health status for data source ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get all active parcel types
   * Returns parcel types with their handler text, shortcut keys, and draw types
   * @param layerCode - Optional layer code to filter by (e.g., 'ROAD_PRCLS')
   */
  async getParcelTypes(layerCode?: string): Promise<any> {
    try {
      const params = layerCode ? `?layer_code=${encodeURIComponent(layerCode)}` : '';
      const response = await this.get(`/erp-map/parcel-types${params}`);
      return response;
    } catch (error) {
      console.error('Error fetching parcel types:', error);
      throw error;
    }
  }

  /**
   * Get parcel categories by parcel type ID
   * Returns categories for a specific parcel type (roads, buildings, water bodies, vacant lands)
   */
  async getParcelCategoriesByType(prclTypeId: number): Promise<any> {
    try {
      const response = await this.get(`/erp-map/parcel-categories/${prclTypeId}`);
      return response;
    } catch (error) {
      console.error(`Error fetching parcel categories for type ${prclTypeId}:`, error);
      throw error;
    }
  }

  /**
   * Get sub-categories grouped by parcel type
   * Returns all parcel types with their associated categories
   */
  async getSubCategoriesByType(): Promise<any> {
    try {
      const response = await this.get(`/erp-map/parcel-types/sub-categories`);
      return response;
    } catch (error) {
      console.error('Error fetching sub-categories by type:', error);
      throw error;
    }
  }

  /**
   * Get compass directions
   * Returns all active compass directions (N, NE, E, SE, S, SW, W, NW)
   */
  async getCompassDirections(): Promise<any> {
    try {
      const response = await this.get(`/erp-map/compass-directions`);
      return response;
    } catch (error) {
      console.error('Error fetching compass directions:', error);
      throw error;
    }
  }

  /**
   * Get land usage list for dropdown
   * Query: select lnd_usg_id, lnd_usg_nm from gis_lnd_usg_lst_t where a_in = true;
   * Optionally filter by prcl_ctgry_id
   */
  async getLandUsageList(prcl_ctgry_id?: number): Promise<any> {
    try {
      const url = prcl_ctgry_id
        ? `/erp-map/land-usage-list?prcl_ctgry_id=${prcl_ctgry_id}`
        : `/erp-map/land-usage-list`;
      const response = await this.get(url);
      return response;
    } catch (error) {
      console.error('Error fetching land usage list:', error);
      throw error;
    }
  }

  /**
   * Get assessment stats (assessed/unassessed counts) by sachivalayam ID
   */
  async getAssessmentStatsBySvmId(svmId: number): Promise<any> {
    try {
      const response = await this.get(`/erp-map/assessment-stats/${svmId}`);
      return response;
    } catch (error) {
      console.error(`Error fetching assessment stats for svm_id ${svmId}:`, error);
      throw error;
    }
  }

  /**
   * Get land parcel assessments by element ID (elmnt_id)
   */
  async getLandParcelAssessmentsByElmntId(elmntId: number): Promise<any> {
    try {
      const response = await this.get(`/erp-map/land-parcel-assessments/${elmntId}`);
      return response;
    } catch (error) {
      console.error(`Error fetching land parcel assessments for elmnt_id ${elmntId}:`, error);
      throw error;
    }
  }

  /**
   * Update land parcel and assessments
   * Updates gis_land_prcl_lst_t and gis_land_prcl_asmnt_lst_t tables
   * @param data - Land parcel data with properties/assessments
   */
  async updateLandParcel(data: {
    elmnt_id: number;
    prcl_ctgry_id?: number | null;
    verification_status?: 'verified' | 'not_verified' | null;
    verification_remarks?: string | null;
    properties?: Array<{
      prcl_asmnt_id?: number | null;
      assessedStatus: 'assessed' | 'unassessed';
      ownerType: 'owner' | 'tenant';
      assessmentNumber: string;
      flatDoorNumber: string;
      floorNumber: number | null;
      doorConfirmed: 'yes' | 'no';
      cmps_drctn_id: number | null;
      ownerName: string;
      mobileNumber: string;
      aadharNumber: string;
    }>;
  }): Promise<any> {
    try {
      const response = await this.put('/erp-map/land-parcels', data);
      return response;
    } catch (error) {
      console.error('Error updating land parcel:', error);
      throw error;
    }
  }

  /**
   * Save gate type and gate geometry info only for a land parcel
   * Updates prcl_gte_typ_id and prcl_map_prts.GATE_TYPE_INFO in gis_land_prcl_lst_t
   * @param data - Gate parcel data with geometry info
   */
  async saveGateParcel(data: {
    elmnt_id: number | string;
    prcl_gte_typ_id?: 1 | 2 | null;
    prcl_map_prts?: {
      GATE_TYPE_INFO?: {
        cmpnd_wll_jsn?: any | null;
        mn_gte_jsn?: any | null;
        mn_gte_lngth?: number | null;
        aprch_pth_jsn?: any | null;
        aprch_pth_lngth?: number | null;
      };
    } | null;
  }): Promise<any> {
    try {
      const response = await this.put('/erp-map/save-gate-parcels', data);
      return response;
    } catch (error) {
      console.error('Error saving gate parcel:', error);
      throw error;
    }
  }

  /**
   * Get existing road-parcel relation for a parcel
   * @param prcl_ds_lyr_id - Parcel ds_lyr_id
   */
  async getRoadParcelRelation(prcl_ds_lyr_id: number): Promise<any> {
    try {
      const response = await this.get(`/erp-map/road-parcel-relation/${prcl_ds_lyr_id}`);
      return response;
    } catch (error) {
      console.error('Error fetching road-parcel relation:', error);
      throw error;
    }
  }

  /**
   * Insert or update road-parcel relation
   * Links a parcel to a road in the gis_rd_prcls_rel_t table
   * If old_rd_ds_lyr_id is provided, deactivates the old relation first
   * @param data - Road and parcel ds_lyr_ids, optionally old road id to deactivate
   */
  async insertRoadParcelRelation(data: {
    rd_ds_lyr_id: number;
    prcl_ds_lyr_id: number;
    old_rd_ds_lyr_id?: number;
  }): Promise<any> {
    try {
      const response = await this.post('/erp-map/road-parcel-relation', data);
      return response;
    } catch (error) {
      console.error('Error inserting road-parcel relation:', error);
      throw error;
    }
  }

  /**
   * Get active departments
   * If prclTypeId is provided, filters by parcel type (gis_prcl_type_id)
   */
  async getDepartments(prclTypeId?: number): Promise<any> {
    try {
      const url = prclTypeId
        ? `/erp-map/departments/${prclTypeId}`
        : '/erp-map/departments';
      const response = await this.get(url);
      return response;
    } catch (error) {
      console.error('Error fetching departments:', error);
      throw error;
    }
  }

  /**
   * Save road data
   */
  async saveRoad(data: any): Promise<any> {
    try {
      const response = await this.post('/erp-map/roads', { data });
      return response;
    } catch (error) {
      console.error('Error saving road:', error);
      throw error;
    }
  }

  /**
   * Update road data
   */
  async updateRoad(data: any): Promise<any> {
    try {
      const response = await this.put('/erp-map/roads', { data });
      return response;
    } catch (error) {
      console.error('Error updating road:', error);
      throw error;
    }
  }

  /**
   * Save water body data
   */
  async saveWaterBody(data: any): Promise<any> {
    try {
      const response = await this.post('/erp-map/waterbodies', { data });
      return response;
    } catch (error) {
      console.error('Error saving water body:', error);
      throw error;
    }
  }

  /**
   * Update water body data
   */
  async updateWaterBody(data: any): Promise<any> {
    try {
      const response = await this.put('/erp-map/waterbodies', { data });
      return response;
    } catch (error) {
      console.error('Error updating water body:', error);
      throw error;
    }
  }

  /**
   * Get water body details by element ID
   */
  async getWaterBodyDetailsByElmntId(elmntId: number): Promise<any> {
    try {
      const response = await this.get(`/erp-map/waterbodies/details/${elmntId}`);
      return response;
    } catch (error) {
      console.error('Error fetching water body details:', error);
      throw error;
    }
  }

  /**
   * Save building data
   */
  async saveBuilding(data: any): Promise<any> {
    try {
      const response = await this.post('/erp-map/buildings', { data });
      return response;
    } catch (error) {
      console.error('Error saving building:', error);
      throw error;
    }
  }

  /**
   * Update building data
   */
  async updateBuilding(data: any): Promise<any> {
    try {
      const response = await this.put('/erp-map/buildings', { data });
      return response;
    } catch (error) {
      console.error('Error updating building:', error);
      throw error;
    }
  }

  /**
   * Delete assessment (for multi-unit buildings)
   * @param asmntId - Assessment ID to delete
   */
  async deleteAssessment(asmntId: number): Promise<any> {
    try {
      const response = await this.delete(`/erp-map/assessment/${asmntId}`);
      return response;
    } catch (error) {
      console.error('Error deleting assessment:', error);
      throw error;
    }
  }

  /**
   * Delete owner from assessment
   * @param relId - Assessment-Person relationship ID to delete
   */
  async deleteOwnerFromAssessment(relId: number): Promise<any> {
    try {
      const response = await this.delete(`/erp-map/owner/${relId}`);
      return response;
    } catch (error) {
      console.error('Error deleting owner from assessment:', error);
      throw error;
    }
  }

  /**
   * Get building types from prcl_ctgry where prcl_type_id = 3
   * Returns: bldng_typ_id, bldng_type_nm, hndlr_tx
   */
  async getBuildingTypes(): Promise<any> {
    try {
      const response = await this.get('/erp-map/building-types');
      return response;
    } catch (error) {
      console.error('Error getting building types:', error);
      throw error;
    }
  }

  /**
   * Get building usages from gis_prcl_usge_lst_t
   * Returns: prcl_usge_id, prcl_usge_nm
   */
  async getBuildingUsages(): Promise<any> {
    try {
      const response = await this.get('/erp-map/building-usages');
      return response;
    } catch (error) {
      console.error('Error getting building usages:', error);
      throw error;
    }
  }

  /**
   * Save vacant land data
   */
  async saveVacantLand(data: any): Promise<any> {
    try {
      const response = await this.post('/erp-map/vacant-lands', { data });
      return response;
    } catch (error) {
      console.error('Error saving vacant land:', error);
      throw error;
    }
  }

  /**
   * Update vacant land data
   */
  async updateVacantLand(data: any): Promise<any> {
    try {
      const response = await this.put('/erp-map/vacant-lands', { data });
      return response;
    } catch (error) {
      console.error('Error updating vacant land:', error);
      throw error;
    }
  }

  /**
   * Get vacant land details by element ID
   * @param elmntId - Element ID
   */
  async getVacantLandDetailsByElmntId(elmntId: number): Promise<any> {
    try {
      const response = await this.get(`/erp-map/vacant-lands/details/${elmntId}`);
      return response;
    } catch (error) {
      console.error('Error fetching vacant land details:', error);
      throw error;
    }
  }

  /**
   * Delete owner from vacant land
   * @param relId - Relationship ID (asmnt_prsn_rel_id)
   */
  async deleteVacantLandOwner(relId: number): Promise<any> {
    try {
      const response = await this.delete(`/erp-map/vacant-lands/owner/${relId}`);
      return response;
    } catch (error) {
      console.error('Error deleting vacant land owner:', error);
      throw error;
    }
  }

  /**
   * Save anchor point data
   */
  async saveAnchorPoint(data: any): Promise<any> {
    try {
      const response = await this.post('/erp-map/anchor-points', { data });
      return response;
    } catch (error) {
      console.error('Error saving anchor point:', error);
      throw error;
    }
  }

  /**
   * Update anchor point data
   */
  async updateAnchorPoint(data: any): Promise<any> {
    try {
      const response = await this.put('/erp-map/anchor-points', { data });
      return response;
    } catch (error) {
      console.error('Error updating anchor point:', error);
      throw error;
    }
  }

  /**
   * Get anchor point details by element ID
   * @param elmntId - Element ID
   */
  async getAnchorPointDetailsByElmntId(elmntId: number): Promise<any> {
    try {
      const response = await this.get(`/erp-map/anchor-points/details/${elmntId}`);
      return response;
    } catch (error) {
      console.error('Error fetching anchor point details:', error);
      throw error;
    }
  }

  /**
   * Save sketch (geometry only without form data)
   */
  async saveSketch(data: any): Promise<any> {
    try {
      const response = await this.post('/erp-map/sketch', { data });
      return response;
    } catch (error) {
      console.error('Error saving sketch:', error);
      throw error;
    }
  }

  /**
   * Save road sketch (geometry only without form data)
   * Supports multiple svm_id values if road spans multiple sachivalayams
   * @param data - { geometry, gis_prcl_type_id, gis_layer_id, prcl_ctgry_id, svm_id (single or array), ulb_id }
   */
  async saveSketchRoads(data: any): Promise<any> {
    try {
      const response = await this.post('/erp-map/sketch/roads', { data });
      return response;
    } catch (error) {
      console.error('Error saving road sketch:', error);
      throw error;
    }
  }

  /**
   * Get all saved sketches (map elements without gis_prcl_id)
   * @param svmId - Optional Sachivalayam ID to filter sketches
   */
  async getSketches(svmId?: number | null): Promise<any> {
    try {
      const url = svmId ? `/erp-map/sketches?svm_id=${svmId}` : '/erp-map/sketches';
      const response = await this.get(url);
      return response;
    } catch (error) {
      console.error('Error fetching sketches:', error);
      throw error;
    }
  }

  /**
   * Get all saved sketches by ULB ID and Sachivalayam ID
   * @param ulbId - ULB ID to filter sketches
   * @param svmId - Sachivalayam ID to filter sketches
   * @param lyrHndlr - Optional layer handler to filter by parcel type
   */
  async getSketchesBySvm(ulbId: number, svmId: number, lyrHndlr?: string): Promise<any> {
    try {
      let url = `/erp-map/sketches/ulb/svm/roads/${ulbId}/${svmId}`;
      if (lyrHndlr) {
        url += `?lyr_hndlr=${encodeURIComponent(lyrHndlr)}`;
      }
      const response = await this.get(url);
      return response;
    } catch (error) {
      console.error('Error fetching sketches by ULB/Sachivalayam:', error);
      throw error;
    }
  }

  /**
   * Get parcel statistics counts (total land parcels, without assessment, assessments without parcels)
   */
  async getParcelStats(): Promise<any> {
    try {
      const response = await this.get('/erp-map/parcel-stats');
      return response;
    } catch (error) {
      console.error('Error fetching parcel stats:', error);
      throw error;
    }
  }

  /**
   * Get all pending GIS records (parcels with frm_sbmt_in = false or null)
   * Returns records grouped by ds_lyr_id with assessment properties
   */
  async getPendingRecords(): Promise<any> {
    try {
      const response = await this.get('/erp-map/pending-records');
      return response;
    } catch (error) {
      console.error('Error fetching pending records:', error);
      throw error;
    }
  }

  /**
   * Update sketch geometry
   */
  async updateSketch(data: any): Promise<any> {
    try {
      const response = await this.put('/erp-map/sketch', { data });
      return response;
    } catch (error) {
      console.error('Error updating sketch:', error);
      throw error;
    }
  }

  /**
   * Delete sketch (soft delete)
   */
  async deleteSketch(elmntId: number, gisLyrId: number): Promise<any> {
    try {
      const response = await this.delete(`/erp-map/sketch/${gisLyrId}/${elmntId}`);
      return response;
    } catch (error) {
      console.error('Error deleting sketch:', error);
      throw error;
    }
  }

  /**
   * Perform polygon operation (merge, subtract, cut) using PostGIS
   * @param data Operation data containing:
   *   - operation: 'merge' | 'subtract' | 'cut'
   *   - gis_lyr_id: GIS layer ID
   *   - source_elmnt_id: ID of source polygon
   *   - source_geometry: GeoJSON geometry of source polygon
   *   - target_elmnt_id: ID of target polygon (for merge/subtract)
   *   - target_geometry: GeoJSON geometry of target polygon (for merge/subtract)
   *   - cut_line: Array of [lng, lat] points for cut operation
   *     - Two-point: [[lng1, lat1], [lng2, lat2]] - straight line cut
   *     - Boundary-cut: [[lng1, lat1], ..., [lngN, latN]] - multi-point line with angles
   *     - Closed polyline: [[lng1, lat1], ..., [lng1, lat1]] (first == last) - polygon cut
   *   - gis_prcl_type_id: Parcel type ID (optional for cut - new polygon)
   *   - target_parcel: 'drawn' | 'remaining' (for polyline cut - which area becomes new parcel)
   */
  async polygonOperation(data: {
    operation: 'merge' | 'subtract' | 'cut';
    gis_lyr_id: number | null;
    source_elmnt_id: number;
    source_geometry: any;
    target_elmnt_id?: number;
    target_geometry?: any;
    cut_line?: [number, number][];
    gis_prcl_type_id?: number;
    target_parcel?: 'drawn' | 'remaining';
  }): Promise<any> {
    try {
      const response = await this.put('/erp-map/polygon-operation', { data });
      return response;
    } catch (error) {
      console.error('Error performing polygon operation:', error);
      throw error;
    }
  }

  /**
   * Get all active risk categories for buffer zone creation
   */
  async getRiskCategories(): Promise<any> {
    try {
      const response = await this.get('/erp-map/risk-categories');
      return response;
    } catch (error) {
      console.error('Error fetching risk categories:', error);
      throw error;
    }
  }

  /**
   * Get all active disaster types for buffer zone creation
   */
  async getDisasterTypes(): Promise<any> {
    try {
      const response = await this.get('/erp-map/disaster-types');
      return response;
    } catch (error) {
      console.error('Error fetching disaster types:', error);
      throw error;
    }
  }

  /**
   * Get all active officer designations for buffer zone assignment
   */
  async getDesignations(): Promise<any> {
    try {
      const response = await this.get('/erp-map/designations');
      return response;
    } catch (error) {
      console.error('Error fetching designations:', error);
      throw error;
    }
  }

  /**
   * Create buffer zone with risk categories
   * @param data Buffer zone data containing:
   *   - elmnt_id: Element ID
   *   - bfr_zn_nm: Buffer zone name
   *   - bfr_zn_rng_ct: Zone range in meters
   *   - dsastr_id: Disaster type ID
   *   - bfr_zn_bdy_jsn: GeoJSON geometry
   *   - hlp_ln_nu: Helpline number (optional)
   *   - asgn_ofcr_dsg_id: Assign officer designation ID (optional)
   *   - risk_entries: Array of risk category entries
   */
  async createBufferzone(data: {
    elmnt_id: number;
    bfr_zn_nm: string;
    bfr_zn_rng_ct: number;
    dsastr_id: number;
    bfr_zn_bdy_jsn: any;
    hlp_ln_nu?: string | null;
    asgn_ofcr_dsg_id?: number | null;
    risk_entries: {
      rsk_ctgry_id: number;
      rsk_mn_rng: number;
      rsk_mx_rng: number;
      rsk_clr_txt: string;
    }[];
  }): Promise<any> {
    try {
      const response = await this.post('/erp-map/bufferzone', { data });
      return response;
    } catch (error) {
      console.error('Error creating buffer zone:', error);
      throw error;
    }
  }

  /**
   * Get buffer zone by element ID
   * Returns buffer zone details with risk categories if exists, null otherwise
   */
  async getBufferzoneByElmntId(elmntId: number): Promise<any> {
    try {
      const response = await this.get(`/erp-map/bufferzone/${elmntId}`);
      return response;
    } catch (error) {
      console.error('Error fetching buffer zone:', error);
      throw error;
    }
  }

  /**
   * Update buffer zone with risk categories
   * @param data Buffer zone update data containing:
   *   - bfr_zn_id: Buffer zone ID
   *   - bfr_zn_nm: Buffer zone name
   *   - bfr_zn_rng_ct: Zone range in meters
   *   - dsastr_id: Disaster type ID
   *   - bfr_zn_bdy_jsn: GeoJSON geometry (optional)
   *   - hlp_ln_nu: Helpline number (optional)
   *   - asgn_ofcr_dsg_id: Assign officer designation ID (optional)
   *   - risk_entries: Array of risk category entries
   */
  async updateBufferzone(data: {
    bfr_zn_id: number;
    bfr_zn_nm: string;
    bfr_zn_rng_ct: number;
    dsastr_id: number;
    bfr_zn_bdy_jsn?: any;
    hlp_ln_nu?: string | null;
    asgn_ofcr_dsg_id?: number | null;
    risk_entries: {
      rsk_ctgry_id: number;
      rsk_mn_rng: number;
      rsk_mx_rng: number;
      rsk_clr_txt: string;
    }[];
  }): Promise<any> {
    try {
      const response = await this.put('/erp-map/bufferzone', { data });
      return response;
    } catch (error) {
      console.error('Error updating buffer zone:', error);
      throw error;
    }
  }

  // ================================================================
  // SACHIVALAYAM BOUNDARY METHODS (PBF Vector Tiles)
  // ================================================================

  /**
   * Get Sachivalayam boundary tile info (returns URL to PBF file)
   * The PBF tile is auto-generated if it doesn't exist
   */
  async getSachivalayamBoundary(): Promise<any> {
    try {
      const response = await this.get('/erp-map/sachivalayam-boundary');
      return response;
    } catch (error) {
      console.error('Error fetching sachivalayam boundary info:', error);
      throw error;
    }
  }

  /**
   * Get Sachivalayam PBF tile URL pattern
   * Used for adding vector tile source to map
   * @param svmCode - Sachivalayam code
   */
  getSachivalayamPbfTileUrl(svmCode: number | string): string {
    return `http://localhost:4901/api/erp-map/sachivalayam-boundary/mvt/${svmCode}/{z}/{x}/{y}.pbf`;
  }

  /**
   * Get Sachivalayam boundary as GeoJSON (for mask layer)
   * Returns polygon geometry for creating mask outside boundary
   */
  async getSachivalayamBoundaryGeoJSON(): Promise<any> {
    try {
      const response = await this.get('/erp-map/sachivalayam-boundary/geojson');
      return response;
    } catch (error) {
      console.error('Error fetching sachivalayam boundary GeoJSON:', error);
      throw error;
    }
  }

  /**
   * Get list of ULBs with boundary data
   * Returns ULBs that have sachivalayam with boundary data for dropdown selection
   */
  async getULBListWithBounds(): Promise<any> {
    try {
      const response = await this.get('/erp-map/ulb-list');
      return response;
    } catch (error) {
      console.error('Error fetching ULB list with bounds:', error);
      throw error;
    }
  }

  /**
   * Get ULB boundary as GeoJSON (combined sachivalayam boundaries)
   * @param ulbId - ULB ID to fetch boundary for
   */
  async getULBBoundaryGeoJSON(ulbId: number): Promise<any> {
    try {
      const response = await this.get(`/erp-map/ulb-boundary/${ulbId}/geojson`);
      return response;
    } catch (error) {
      console.error(`Error fetching ULB boundary GeoJSON for ulb_id ${ulbId}:`, error);
      throw error;
    }
  }

  /**
   * Get user's ULB boundary as GeoJSON based on primary boundary
   * Uses authenticated user's ULB from their primary boundary
   */
  async getUserULBBoundaryGeoJSON(): Promise<any> {
    try {
      const response = await this.get('/erp-map/user-ulb-boundary/geojson');
      return response;
    } catch (error) {
      console.error('Error fetching user ULB boundary GeoJSON:', error);
      throw error;
    }
  }

  /**
   * Get ULB boundary tile info (returns URL to MVT tiles)
   * @param ulbId - ULB ID to fetch tile info for
   */
  async getULBTileInfo(ulbId: number): Promise<any> {
    try {
      const response = await this.get(`/erp-map/ulb-boundary/${ulbId}/tile-info`);
      return response;
    } catch (error) {
      console.error(`Error fetching ULB tile info for ulb_id ${ulbId}:`, error);
      throw error;
    }
  }

  /**
   * Create and cache ULB PBF vector tile
   * Generates MVT tile using PostGIS and saves to /vector-tiles/ulb/
   * @param ulbId - ULB ID to create PBF for
   */
  async createULBVectorTilePbf(ulbId: number): Promise<any> {
    try {
      const response = await this.post(`/erp-map/ulb-boundary/${ulbId}/create-pbf`, {});
      return response;
    } catch (error) {
      console.error(`Error creating ULB PBF tile for ulb_id ${ulbId}:`, error);
      throw error;
    }
  }

  /**
   * Get parcel details by element ID
   * Returns detailed parcel information including submission status
   * @param elmntId - Element ID to fetch details for
   */
  async getParcelDetailsByElmntId(elmntId: number): Promise<any> {
    try {
      const response = await this.get(`/erp-map/parcel-details/${elmntId}`);
      return response;
    } catch (error) {
      console.error(`Error fetching parcel details for elmnt_id ${elmntId}:`, error);
      throw error;
    }
  }

  /**
   * Get building details by element ID
   * Returns building-specific information including:
   * - Basic parcel info (name, category, area)
   * - Assessment details (assessment number, unassessed status)
   * - Owner details from gis_prsn_lst_t
   * - Area size details with unit information
   * - Attachments
   * @param elmntId - Element ID to fetch building details for
   */
  async getBuildingDetailsByElmntId(elmntId: number): Promise<any> {
    try {
      const response = await this.get(`/erp-map/building-details/${elmntId}`);
      return response;
    } catch (error) {
      console.error(`Error fetching building details for elmnt_id ${elmntId}:`, error);
      throw error;
    }
  }

  /**
   * Delete parcel attachment (soft delete)
   * Sets a_in = false for the attachment record
   * @param prclAtchtId - Parcel attachment ID to delete
   */
  async deleteParcelAttachment(prclAtchtId: number): Promise<any> {
    try {
      const response = await this.delete(`/erp-map/attachment/${prclAtchtId}`);
      return response;
    } catch (error) {
      console.error(`Error deleting attachment ${prclAtchtId}:`, error);
      throw error;
    }
  }

  /**
   * Get all active area units
   * Returns list of area units with unt_id, unt_nm, hndlr_tx
   */
  async getAreaUnits(): Promise<any> {
    try {
      const response = await this.get('/erp-map/area-units');
      return response;
    } catch (error) {
      console.error('Error fetching area units:', error);
      throw error;
    }
  }

  /**
   * Get all active floors
   * Returns list of floors with flr_id, flr_nm
   */
  async getFloors(): Promise<any> {
    try {
      const response = await this.get('/erp-map/floors');
      return response;
    } catch (error) {
      console.error('Error fetching floors:', error);
      throw error;
    }
  }

  /**
   * Get basemap layers for current user's sachivalayam or by ULB ID
   * Returns list of basemap layers with bs_mp_lyr_id, lyr_nm, url_tx
   * @param ulbId - Optional ULB ID to filter by
   * @param svmCode - Optional sachivalayam code (used only if ulbId is provided)
   */
  async getBasemapLayers(ulbId?: number, svmCode?: string): Promise<any> {
    try {
      const params: string[] = [];
      if (ulbId) {
        params.push(`ulb_id=${ulbId}`);
      }
      if (svmCode) {
        params.push(`svm_code=${encodeURIComponent(svmCode)}`);
      }
      const queryString = params.length > 0 ? `?${params.join('&')}` : '';
      const response = await this.get(`/erp-map/basemap-layers${queryString}`);
      return response;
    } catch (error) {
      console.error('Error fetching basemap layers:', error);
      throw error;
    }
  }

  /**
   * Get ERP assessment details from external API
   * @param erpUlbId - ERP ULB ID (ulbCode)
   * @param assessmentNo - Assessment Number
   * Returns property details from ERP system
   */
  async getErpAssessmentDetails(erpUlbId: string, assessmentNo: string): Promise<any> {
    try {
      const response = await this.post('/erp-map/erp-assessment-details', {
        erp_ulb_id: erpUlbId,
        assessment_no: assessmentNo
      });
      return response;
    } catch (error) {
      console.error('Error fetching ERP assessment details:', error);
      throw error;
    }
  }

  /**
   * Get dashboard header counts for state-level dashboard
   * Returns total districts, ULBs, and sachivalayams count
   */
  async getDashboardHeaderCounts(): Promise<any> {
    try {
      const response = await this.get('/erp-map/dashboard-header-counts');
      return response;
    } catch (error) {
      console.error('Error fetching dashboard header counts:', error);
      throw error;
    }
  }

  /**
   * Get dashboard summary for state-level dashboard
   * Returns aggregated parcel statistics grouped by district, ULB, and sachivalayam
   * Used for drill-down: State → District → ULB → Sachivalayam
   */
  async getDashboardSummary(): Promise<any> {
    try {
      const response = await this.get('/erp-map/dashboard-summary');
      return response;
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
      throw error;
    }
  }

  /**
   * Get road category wise summary statistics
   * Returns road data grouped by category (prcl_ctgry_id) for dashboard
   * Used for Road Category section in state dashboard
   */
  async getRoadCategorySummary(): Promise<any> {
    try {
      const response = await this.get('/erp-map/road-category-summary');
      return response;
    } catch (error) {
      console.error('Error fetching road category summary:', error);
      throw error;
    }
  }

  // ============================================
  // ANCHOR POINTS API METHODS
  // ============================================

  /**
   * Get all anchor points
   */
  async getAnchorPoints(): Promise<any> {
    try {
      const response = await this.get('/maps/masters/anchor-points');
      return response;
    } catch (error) {
      console.error('Error fetching anchor points:', error);
      throw error;
    }
  }

  /**
   * Get anchor points by Sachivalayam ID
   */
  async getAnchorPointsBySvmId(svmId: number): Promise<any> {
    try {
      const response = await this.get(`/maps/masters/anchor-points/by-svm/${svmId}`);
      return response;
    } catch (error) {
      console.error(`Error fetching anchor points for svm_id ${svmId}:`, error);
      throw error;
    }
  }

  /**
   * Get anchor points by ULB ID
   */
  async getAnchorPointsByUlbId(ulbId: number): Promise<any> {
    try {
      const response = await this.get(`/maps/masters/anchor-points/by-ulb/${ulbId}`);
      return response;
    } catch (error) {
      console.error(`Error fetching anchor points for ulb_id ${ulbId}:`, error);
      throw error;
    }
  }

  /**
   * Get anchor point by ID
   */
  async getAnchorPointById(id: number): Promise<any> {
    try {
      const response = await this.get(`/maps/masters/anchor-points/${id}`);
      return response;
    } catch (error) {
      console.error(`Error fetching anchor point ${id}:`, error);
      throw error;
    }
  }

  /**
   * Save anchor point (create or update)
   */
  async saveAnchorPoint(anchorPoint: any): Promise<any> {
    try {
      const response = await this.post('/maps/masters/anchor-points', anchorPoint);
      return response;
    } catch (error) {
      console.error('Error saving anchor point:', error);
      throw error;
    }
  }

  /**
   * Delete anchor point by ID
   */
  async deleteAnchorPoint(id: number): Promise<any> {
    try {
      const response = await this.delete(`/maps/masters/anchor-points/${id}`);
      return response;
    } catch (error) {
      console.error(`Error deleting anchor point ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get anchor point types for dropdown
   */
  async getAnchorPointTypes(): Promise<any> {
    try {
      const response = await this.get('/maps/masters/anchor-points/types');
      return response;
    } catch (error) {
      console.error('Error fetching anchor point types:', error);
      throw error;
    }
  }

  /**
   * Generate next anchor point code
   */
  async generateNextAnchorPointCode(): Promise<any> {
    try {
      const response = await this.get('/maps/masters/anchor-points/next-code');
      return response;
    } catch (error) {
      console.error('Error generating next anchor point code:', error);
      throw error;
    }
  }

  // =====================================================
  // ANCHOR POINT - ROAD RELATIONSHIP METHODS
  // =====================================================

  /**
   * Find roads at a given lat/lng point filtered by ULB ID
   * Used to check if user clicked on a road when adding anchor point
   */
  async getRoadsAtPoint(lat: number, lng: number, ulbId: number): Promise<any> {
    try {
      const response = await this.get(`/maps/masters/anchor-points/roads-at-point?lat=${lat}&lng=${lng}&ulb_id=${ulbId}`);
      return response;
    } catch (error) {
      console.error('Error finding roads at point:', error);
      throw error;
    }
  }

  /**
   * Get all roads linked to an anchor point
   */
  async getAnchorPointRoads(anchorPointId: number): Promise<any> {
    try {
      const response = await this.get(`/maps/masters/anchor-points/${anchorPointId}/roads`);
      return response;
    } catch (error) {
      console.error(`Error fetching roads for anchor point ${anchorPointId}:`, error);
      throw error;
    }
  }

  /**
   * Save anchor point with road relationships
   * @param anchorPoint - Anchor point data including road_ids array
   */
  async saveAnchorPointWithRoads(anchorPoint: any): Promise<any> {
    try {
      const response = await this.post('/maps/masters/anchor-points/with-roads', anchorPoint);
      return response;
    } catch (error) {
      console.error('Error saving anchor point with roads:', error);
      throw error;
    }
  }

  /**
   * Update anchor point with road relationships
   * @param id - Anchor point ID
   * @param anchorPoint - Anchor point data including road_ids array
   */
  async updateAnchorPointWithRoads(id: number, anchorPoint: any): Promise<any> {
    try {
      const response = await this.put(`/maps/masters/anchor-points/${id}/with-roads`, anchorPoint);
      return response;
    } catch (error) {
      console.error(`Error updating anchor point ${id} with roads:`, error);
      throw error;
    }
  }

  async getPropertiesList(): Promise<any> {
    try {
      const response = await this.get('/erp-map/properties-list');
      return response;
    } catch (error) {
      console.error('Error fetching properties list:', error);
      throw error;
    }
  }

  async getUlbWisePropertiesList(svmIds: number[]): Promise<any> {
    try {
      const response = await this.post('/erp-map/ulb-wise-properties-list', { svm_ids: svmIds });
      return response;
    } catch (error) {
      console.error('Error fetching ULB-wise properties list:', error);
      throw error;
    }
  }

  async unmapParcel(data: { svm_id: number; ulb_id: number; prcl_ctgry_id: number; prcl_asmnt_id: number }): Promise<any> {
    try {
      const response = await this.post('/erp-map/unmap-parcel', { data });
      return response;
    } catch (error) {
      console.error('Error unmapping parcel:', error);
      throw error;
    }
  }

  /**
   * Get road network details by element ID (ds_lyr_id)
   * Returns comprehensive road information from road_network table
   * @param elmntId - Element ID (ds_lyr_id) of the road
   */
  async getRoadNetworkDetailsByElmntId(elmntId: number): Promise<any> {
    try {
      const response = await this.get(`/erp-map/road-network-details/${elmntId}`);
      return response;
    } catch (error) {
      console.error(`Error fetching road network details for elmnt_id ${elmntId}:`, error);
      throw error;
    }
  }

  /**
   * Update road network details by element ID (ds_lyr_id)
   * @param elmntId - Element ID (ds_lyr_id) of the road
   * @param data - Updated road data
   */
  async updateRoadNetworkDetails(elmntId: number, data: any): Promise<any> {
    try {
      const response = await this.put(`/erp-map/road-network-details/${elmntId}`, data);
      return response;
    } catch (error) {
      console.error(`Error updating road network details for elmnt_id ${elmntId}:`, error);
      throw error;
    }
  }

  /**
   * Get list of road maintainers (R&B, PR&RD, ULB)
   */
  async getRoadMaintainers(): Promise<any> {
    try {
      const response = await this.get('/erp-map/road-maintainers');
      return response;
    } catch (error) {
      console.error('Error fetching road maintainers:', error);
      throw error;
    }
  }

  /**
   * Get list of road categories with hierarchy (NH, SH, MDR, etc.)
   */
  async getRoadCategories(): Promise<any> {
    try {
      const response = await this.get('/erp-map/road-categories');
      return response;
    } catch (error) {
      console.error('Error fetching road categories:', error);
      throw error;
    }
  }

  /**
   * Get list of road conditions for dropdown
   */
  async getRoadConditions(): Promise<any> {
    try {
      const response = await this.get('/erp-map/road-conditions');
      return response;
    } catch (error) {
      console.error('Error fetching road conditions:', error);
      throw error;
    }
  }

  /**
   * Get list of road construction materials for dropdown
   */
  async getRoadMaterials(): Promise<any> {
    try {
      const response = await this.get('/erp-map/road-materials');
      return response;
    } catch (error) {
      console.error('Error fetching road materials:', error);
      throw error;
    }
  }

  // ================================================================
  // COMBINED ROADS MANAGEMENT
  // ================================================================

  /**
   * Get all combined roads
   * @param ulbId - Optional ULB ID to filter combined roads
   */
  async getCombinedRoads(ulbId?: number): Promise<any> {
    try {
      const url = ulbId ? `/erp-map/combined-roads?ulb_id=${ulbId}` : '/erp-map/combined-roads';
      const response = await this.get(url);
      return response;
    } catch (error) {
      console.error('Error fetching combined roads:', error);
      throw error;
    }
  }

  /**
   * Get combined road by ID with all parts
   * @param id - Combined road ID
   */
  async getCombinedRoadById(id: number): Promise<any> {
    try {
      const response = await this.get(`/erp-map/combined-roads/${id}`);
      return response;
    } catch (error) {
      console.error(`Error fetching combined road ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new combined road
   * @param data - Combined road data:
   *   - rd_nm: Road name
   *   - rd_cd: Road code (optional)
   *   - rd_ctgry_id: Road category ID
   *   - category_code: Road category code (optional)
   *   - rd_mntnr_id: Road maintainer ID
   *   - ulb_id: ULB ID
   *   - ulb_code: ULB code (optional)
   *   - part_ids: Array of road network ds_lyr_id values to include as parts
   */
  async createCombinedRoad(data: {
    rd_nm: string;
    rd_cd?: string;
    rd_ctgry_id: number;
    category_code?: string;
    rd_mntnr_id: number;
    ulb_id: number;
    ulb_code?: string;
    part_ids?: number[];
  }): Promise<any> {
    try {
      // Map to backend field names
      const backendData = {
        road_name: data.rd_nm,
        road_code: data.rd_cd,
        category_id: data.rd_ctgry_id,
        category_code: data.category_code,
        maintainer_id: data.rd_mntnr_id,
        ulb_id: data.ulb_id,
        ulb_code: data.ulb_code,
        parts: data.part_ids?.map((id, index) => ({
          road_network_id: id,
          sequence: index + 1
        }))
      };
      const response = await this.post('/erp-map/combined-roads', backendData);
      return response;
    } catch (error) {
      console.error('Error creating combined road:', error);
      throw error;
    }
  }

  /**
   * Update a combined road
   * @param id - Combined road ID
   * @param data - Updated combined road data
   */
  async updateCombinedRoad(id: number, data: {
    rd_nm?: string;
    rd_cd?: string;
    rd_ctgry_id?: number;
    category_code?: string;
    rd_mntnr_id?: number;
    ulb_code?: string;
  }): Promise<any> {
    try {
      // Map to backend field names
      const backendData = {
        road_name: data.rd_nm,
        road_code: data.rd_cd,
        category_id: data.rd_ctgry_id,
        category_code: data.category_code,
        maintainer_id: data.rd_mntnr_id,
        ulb_code: data.ulb_code
      };
      const response = await this.put(`/erp-map/combined-roads/${id}`, backendData);
      return response;
    } catch (error) {
      console.error(`Error updating combined road ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a combined road (soft delete)
   * @param id - Combined road ID
   */
  async deleteCombinedRoad(id: number): Promise<any> {
    try {
      const response = await this.delete(`/erp-map/combined-roads/${id}`);
      return response;
    } catch (error) {
      console.error(`Error deleting combined road ${id}:`, error);
      throw error;
    }
  }

  /**
   * Add a road part to a combined road
   * @param combinedRoadId - Combined road ID
   * @param data - Part data:
   *   - rd_ntwrk_id: Road network ds_lyr_id
   *   - sqnc_nu: Sequence number (optional)
   */
  async addRoadPart(combinedRoadId: number, data: {
    rd_ntwrk_id: number;
    sqnc_nu?: number;
  }): Promise<any> {
    try {
      // Map to backend field names
      const backendData = {
        road_network_id: data.rd_ntwrk_id,
        sequence: data.sqnc_nu
      };
      const response = await this.post(`/erp-map/combined-roads/${combinedRoadId}/parts`, backendData);
      return response;
    } catch (error) {
      console.error(`Error adding road part to combined road ${combinedRoadId}:`, error);
      throw error;
    }
  }

  /**
   * Remove a road part from a combined road
   * @param combinedRoadId - Combined road ID
   * @param partId - Road part relationship ID
   */
  async removeRoadPart(combinedRoadId: number, partId: number): Promise<any> {
    try {
      const response = await this.delete(`/erp-map/combined-roads/${combinedRoadId}/parts/${partId}`);
      return response;
    } catch (error) {
      console.error(`Error removing road part ${partId} from combined road ${combinedRoadId}:`, error);
      throw error;
    }
  }

  /**
   * Sync road parts for a combined road - add new parts and remove old parts in one call
   * @param combinedRoadId - Combined road ID
   * @param data - Sync data:
   *   - add_part_ids: Array of rd_ntwrk_ids to add
   *   - remove_part_ids: Array of rd_ntwrk_ids to remove (inactivate)
   */
  async syncRoadParts(combinedRoadId: number, data: {
    add_part_ids?: number[];
    remove_part_ids?: number[];
  }): Promise<any> {
    try {
      const response = await this.post(`/erp-map/combined-roads/${combinedRoadId}/parts/sync`, data);
      return response;
    } catch (error) {
      console.error(`Error syncing road parts for combined road ${combinedRoadId}:`, error);
      throw error;
    }
  }

  /**
   * Approve a combined road
   * @param combinedRoadId - Combined road ID
   * @param data - Approval data (comment)
   */
  async approveCombinedRoad(combinedRoadId: number, data: {
    aprv_cmnt_txt?: string;
  }): Promise<any> {
    try {
      const response = await this.post(`/erp-map/combined-roads/${combinedRoadId}/approve`, data);
      return response;
    } catch (error) {
      console.error(`Error approving combined road ${combinedRoadId}:`, error);
      throw error;
    }
  }

  /**
   * Search road network parts for combining
   * @param query - Search query string
   * @param ulbId - ULB ID to filter roads
   */
  async searchRoadParts(query: string, ulbId?: number): Promise<any> {
    try {
      const params = new URLSearchParams();
      if (query) params.append('q', query);
      if (ulbId) params.append('ulb_id', ulbId.toString());
      const url = `/erp-map/road-parts/search?${params.toString()}`;
      const response = await this.get(url);
      return response;
    } catch (error) {
      console.error('Error searching road parts:', error);
      throw error;
    }
  }

  /**
   * Map an assessment to a parcel
   * Verifies if assessment is already mapped to another parcel and handles the mapping
   * @param data - Mapping data:
   *   - ds_lyr_id: Target parcel ds_lyr_id
   *   - prcl_asmnt_id: Assessment ID to map
   *   - force: If true, will unmap from existing parcel and map to new one
   */
  async mapAssessmentToParcel(data: {
    ds_lyr_id: number;
    prcl_asmnt_id: number;
    force?: boolean;
  }): Promise<any> {
    try {
      const response = await this.post('/erp-map/map-assessment-to-parcel', data);
      return response;
    } catch (error) {
      console.error('Error mapping assessment to parcel:', error);
      throw error;
    }
  }

  /**
   * Get block master list for a land parcel
   * Table: gis_lnd_prcl_blk_mstr_rel_t
   * @param dsLyrId - The parcel ds_lyr_id
   */
  async getBlockMasterList(dsLyrId: number): Promise<any> {
    try {
      const response = await this.get(`/erp-map/block-master/${dsLyrId}`);
      return response;
    } catch (error) {
      console.error(`Error fetching block master list for ds_lyr_id ${dsLyrId}:`, error);
      throw error;
    }
  }

  /**
   * Save block master list for a land parcel (insert/update/delete)
   * Table: gis_lnd_prcl_blk_mstr_rel_t
   * @param data - Block master data:
   *   - prcl_ds_lyr_id: The parcel ds_lyr_id
   *   - blocks: Array of block objects with prcl_blk_id, blk_nm, blk_cd, isNew, isDeleted
   */
  async saveBlockMasterList(data: {
    prcl_ds_lyr_id: number;
    blocks: Array<{
      prcl_blk_id?: number;
      blk_nm: string;
      blk_cd: string;
      isNew?: boolean;
      isDeleted?: boolean;
    }>;
  }): Promise<any> {
    try {
      const response = await this.post('/erp-map/block-master', data);
      return response;
    } catch (error) {
      console.error('Error saving block master list:', error);
      throw error;
    }
  }

  // ============================================
  // Trade Details APIs
  // Table: gis_prcl_trd_dtl_t
  // ============================================

  /**
   * Get business types list for dropdown
   * Table: gis_bsns_typ_lst_t
   */
  async getBusinessTypesList(): Promise<any> {
    try {
      const response = await this.get('/erp-map/business-types');
      return response;
    } catch (error) {
      console.error('Error fetching business types list:', error);
      throw error;
    }
  }

  /**
   * Get trade details list for a parcel
   * Table: gis_prcl_trd_dtl_t
   * @param dsLyrId - The parcel ds_lyr_id
   */
  async getTradeDetailsList(dsLyrId: number): Promise<any> {
    try {
      const response = await this.get(`/erp-map/trade-details/${dsLyrId}`);
      return response;
    } catch (error) {
      console.error(`Error fetching trade details for ds_lyr_id ${dsLyrId}:`, error);
      throw error;
    }
  }

  /**
   * Get all trade details for the user's sachivalayam (svm_cd from user context)
   * Table: gis_prcl_trd_dtl_t
   */
  async getAllTradeDetailsBySvm(): Promise<any> {
    try {
      const response = await this.get('/erp-map/trade-details-by-svm');
      return response;
    } catch (error) {
      console.error('Error fetching trade details by svm:', error);
      throw error;
    }
  }

  /**
   * Create a new trade detail entry
   * Table: gis_prcl_trd_dtl_t
   * @param data - Trade detail data
   */
  async createTradeDetail(data: {
    prcl_ds_lyr_id: number;
    trd_nm: string;
    trd_type?: string;
    ownr_nm?: string;
    ownr_mbl_phn?: string;
    email_txt?: string;
    trd_logo?: string;
  }): Promise<any> {
    try {
      const response = await this.post('/erp-map/trade-details', data);
      return response;
    } catch (error) {
      console.error('Error creating trade detail:', error);
      throw error;
    }
  }

  /**
   * Update an existing trade detail entry
   * Table: gis_prcl_trd_dtl_t
   * @param tradeId - The prcl_trd_id
   * @param data - Trade detail data
   */
  async updateTradeDetail(tradeId: number, data: {
    prcl_ds_lyr_id: number;
    trd_nm: string;
    trd_type?: string;
    ownr_nm?: string;
    ownr_mbl_phn?: string;
    email_txt?: string;
    trd_logo?: string;
  }): Promise<any> {
    try {
      const response = await this.put(`/erp-map/trade-details/${tradeId}`, data);
      return response;
    } catch (error) {
      console.error(`Error updating trade detail ${tradeId}:`, error);
      throw error;
    }
  }

  /**
   * Delete a trade detail entry
   * Table: gis_prcl_trd_dtl_t
   * @param tradeId - The prcl_trd_id
   */
  async deleteTradeDetail(tradeId: number): Promise<any> {
    try {
      const response = await this.delete(`/erp-map/trade-details/${tradeId}`);
      return response;
    } catch (error) {
      console.error(`Error deleting trade detail ${tradeId}:`, error);
      throw error;
    }
  }

  /**
   * Check if user has access to map verification screen
   * Query: SELECT asgn_map_vrfctn_in, map_vrfy_ofcr_in, ndl_ofcr_in, cmnd_rd_apvr_in
   *        FROM gis_vrfctn_scrn_alw_empl_dlt_t WHERE emp_id = ? AND a_in = true
   * Returns: { hasAccess, asgn_map_vrfctn_in, map_vrfy_ofcr_in, ndl_ofcr_in, cmnd_rd_apvr_in }
   */
  async checkMapVerificationAccess(): Promise<{
    status: number;
    message: string | null;
    data: {
      hasAccess: boolean;
      asgn_map_vrfctn_in: boolean;
      map_vrfy_ofcr_in: boolean;
      ndl_ofcr_in: boolean;
      cmnd_rd_apvr_in: boolean;
    } | null;
  }> {
    try {
      const response = await this.get('/erp-map/check-verification-access');
      return response;
    } catch (error) {
      console.error('Error checking map verification access:', error);
      throw error;
    }
  }

  /**
   * Update layer_settings on gis_layers_map_rel_t by relationship ID
   */
  async updateLayerMapRelSettings(relId: number, layerSettings: any, isVisibleDefault?: boolean): Promise<any> {
    try {
      const body: any = { layer_settings: layerSettings };
      if (isVisibleDefault !== undefined) body.is_visible_default = isVisibleDefault;
      const response = await this.put(`/gis/layers-map-rel/${relId}/settings`, body);
      return response.data;
    } catch (error) {
      console.error('Error updating layer map rel settings:', error);
      throw error;
    }
  }

  /**
   * Get layer summary stats per sachivalayam for a given ULB
   * Returns per-sachivalayam stats (lyr_id = 80), client calculates totals
   * @param ulbId - ULB ID
   */
  async getLayerSummaryStats(ulbId: number): Promise<any> {
    try {
      const response = await this.get(`/erp-map/layer-summary-stats/${ulbId}`);
      return response;
    } catch (error) {
      console.error('Error fetching layer summary stats:', error);
      throw error;
    }
  }

  /**
   * Get SVM relation layers grouped by layer_group_id
   * Returns layers with svm_rel_tbl_nm for mapping
   */
  async getSvmRelationLayers(): Promise<any> {
    try {
      const response = await this.get('/erp-map/svm-relation-layers');
      return response;
    } catch (error) {
      console.error('Error fetching SVM relation layers:', error);
      throw error;
    }
  }

  /**
   * Get layer master data by executing dynamic map query
   * @param lyrId - Layer ID
   * @param svmId - Sachivalayam ID
   */
  async getSvmRelationLayerDetails(lyrId: number, svmId: number): Promise<any> {
    try {
      const response = await this.post('/erp-map/svm-relation-layer/master/layer/details', {
        lyr_id: lyrId,
        svm_id: svmId
      });
      return response;
    } catch (error) {
      console.error(`Error fetching layer details for lyr_id ${lyrId}, svm_id ${svmId}:`, error);
      throw error;
    }
  }

  // ============================================
  // BOUNDARY API METHODS (for State Dashboard Map View)
  // ============================================

  /**
   * Get state boundary as GeoJSON
   * Returns state boundary for initial map bounds
   */
  async getStateBoundary(): Promise<any> {
    try {
      const response = await this.get('/erp-map/state-boundary');
      return response;
    } catch (error) {
      console.error('Error fetching state boundary:', error);
      throw error;
    }
  }

  /**
   * Get district boundaries as GeoJSON
   * Returns all active districts with their boundary geometry
   */
  async getDistrictBoundaries(): Promise<any> {
    try {
      const response = await this.get('/erp-map/district-boundaries');
      return response;
    } catch (error) {
      console.error('Error fetching district boundaries:', error);
      throw error;
    }
  }

  /**
   * Get ULB boundaries as GeoJSON for a specific district
   * @param districtId - The district ID to filter ULBs
   */
  async getUlbBoundaries(districtId: number): Promise<any> {
    try {
      const response = await this.get(`/erp-map/ulb-boundaries/${districtId}`);
      return response;
    } catch (error) {
      console.error(`Error fetching ULB boundaries for district ${districtId}:`, error);
      throw error;
    }
  }

  /**
   * Get Sachivalayam boundaries as GeoJSON for a specific ULB
   * @param ulbId - The ULB ID to filter Sachivalayams
   */
  async getSachivalayamBoundaries(ulbId: number): Promise<any> {
    try {
      const response = await this.get(`/erp-map/sachivalayam-boundaries/${ulbId}`);
      return response;
    } catch (error) {
      console.error(`Error fetching Sachivalayam boundaries for ULB ${ulbId}:`, error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const gisService = new GISService();
export default gisService;
